import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Receipt, 
  CreditCard, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  DollarSign,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/StatusBadge';

// Interfaces para os tipos de dados
interface InvoiceStats {
  totalInvoices: number;
  paidInvoices: number;
  pendingInvoices: number;
  overdueInvoices: number;
  totalRevenue: number;
  averageValue: number;
}

interface InvoiceStatusData {
  name: string;
  value: number;
  color: string;
}

interface RecentInvoice {
  id: string;
  invoice_number: string;
  customer_name: string;
  amount: number;
  status: string;
  due_date: string;
  created_at: string;
}

interface MonthlyRevenue {
  month: string;
  revenue: number;
}

const InvoicesDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<InvoiceStats>({
    totalInvoices: 0,
    paidInvoices: 0,
    pendingInvoices: 0,
    overdueInvoices: 0,
    totalRevenue: 0,
    averageValue: 0
  });
  const [statusData, setStatusData] = useState<InvoiceStatusData[]>([]);
  const [recentInvoices, setRecentInvoices] = useState<RecentInvoice[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenue[]>([]);

  // Cores para os gráficos
  const statusColors = {
    paid: '#10b981',
    pending: '#f59e0b',
    overdue: '#ef4444',
    draft: '#6b7280'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchInvoiceStats(),
        fetchStatusDistribution(),
        fetchRecentInvoices(),
        fetchMonthlyRevenue()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do dashboard de faturas');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceStats = async () => {
    try {
      // Simulando dados de faturas - em um ambiente real, isso viria do banco de dados
      // Aqui estamos usando dados fictícios para demonstração
      
      // Em um ambiente real, você faria algo como:
      // const { data, error } = await supabase
      //   .from('invoices')
      //   .select('*');
      
      // Dados simulados
      const totalInvoices = 156;
      const paidInvoices = 98;
      const pendingInvoices = 42;
      const overdueInvoices = 16;
      const totalRevenue = 287650.75;
      
      setStats({
        totalInvoices,
        paidInvoices,
        pendingInvoices,
        overdueInvoices,
        totalRevenue,
        averageValue: totalRevenue / totalInvoices
      });
    } catch (error) {
      console.error('Erro ao buscar estatísticas de faturas:', error);
      throw error;
    }
  };

  const fetchStatusDistribution = async () => {
    try {
      // Dados simulados para distribuição de status
      const data: InvoiceStatusData[] = [
        { name: 'Pagas', value: 98, color: statusColors.paid },
        { name: 'Pendentes', value: 42, color: statusColors.pending },
        { name: 'Atrasadas', value: 16, color: statusColors.overdue }
      ];
      
      setStatusData(data);
    } catch (error) {
      console.error('Erro ao buscar distribuição de status:', error);
      throw error;
    }
  };

  const fetchRecentInvoices = async () => {
    try {
      // Dados simulados para faturas recentes
      const data: RecentInvoice[] = [
        {
          id: '1',
          invoice_number: 'INV-2023-001',
          customer_name: 'Empresa ABC Ltda',
          amount: 12500.00,
          status: 'paid',
          due_date: '2023-11-15',
          created_at: '2023-11-01'
        },
        {
          id: '2',
          invoice_number: 'INV-2023-002',
          customer_name: 'Corporação XYZ S.A.',
          amount: 8750.50,
          status: 'pending',
          due_date: '2023-11-30',
          created_at: '2023-11-10'
        },
        {
          id: '3',
          invoice_number: 'INV-2023-003',
          customer_name: 'Indústrias 123',
          amount: 15200.75,
          status: 'overdue',
          due_date: '2023-11-05',
          created_at: '2023-10-20'
        },
        {
          id: '4',
          invoice_number: 'INV-2023-004',
          customer_name: 'Tech Solutions',
          amount: 5800.25,
          status: 'paid',
          due_date: '2023-11-10',
          created_at: '2023-10-25'
        },
        {
          id: '5',
          invoice_number: 'INV-2023-005',
          customer_name: 'Global Services',
          amount: 9300.00,
          status: 'pending',
          due_date: '2023-12-05',
          created_at: '2023-11-15'
        }
      ];
      
      setRecentInvoices(data);
    } catch (error) {
      console.error('Erro ao buscar faturas recentes:', error);
      throw error;
    }
  };

  const fetchMonthlyRevenue = async () => {
    try {
      // Dados simulados para receita mensal
      const data: MonthlyRevenue[] = [
        { month: 'Jan', revenue: 42500 },
        { month: 'Fev', revenue: 38700 },
        { month: 'Mar', revenue: 35200 },
        { month: 'Abr', revenue: 37800 },
        { month: 'Mai', revenue: 41300 },
        { month: 'Jun', revenue: 45600 },
        { month: 'Jul', revenue: 48200 },
        { month: 'Ago', revenue: 46900 },
        { month: 'Set', revenue: 49800 },
        { month: 'Out', revenue: 52300 },
        { month: 'Nov', revenue: 54800 },
        { month: 'Dez', revenue: 58700 }
      ];
      
      setMonthlyRevenue(data);
    } catch (error) {
      console.error('Erro ao buscar receita mensal:', error);
      throw error;
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Total de Faturas</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{stats.totalInvoices}</div>
                <p className="text-xs text-muted-foreground">
                  Faturas emitidas
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Receita Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-28" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{formatCurrency(stats.totalRevenue)}</div>
                <p className="text-xs text-muted-foreground">
                  Valor médio: {formatCurrency(stats.averageValue)}
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Faturas Pagas</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{stats.paidInvoices}</div>
                <div className="flex items-center pt-1">
                  <ArrowUpRight className="h-3 w-3 text-green-500 mr-1" />
                  <span className="text-xs text-green-500">
                    {Math.round((stats.paidInvoices / stats.totalInvoices) * 100)}%
                  </span>
                  <span className="text-xs text-muted-foreground ml-1">
                    do total
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Faturas Pendentes/Atrasadas</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{stats.pendingInvoices + stats.overdueInvoices}</div>
                <div className="flex items-center space-x-2 pt-1">
                  <div className="flex items-center">
                    <div className="h-2 w-2 rounded-full bg-yellow-500 mr-1"></div>
                    <span className="text-xs text-muted-foreground">{stats.pendingInvoices} pendentes</span>
                  </div>
                  <div className="flex items-center">
                    <div className="h-2 w-2 rounded-full bg-red-500 mr-1"></div>
                    <span className="text-xs text-muted-foreground">{stats.overdueInvoices} atrasadas</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Distribuição por Status</CardTitle>
            <CardDescription>
              Visão geral do status das faturas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[300px] flex items-center justify-center">
                <Skeleton className="h-[250px] w-[250px] rounded-full" />
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} faturas`, '']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Receita Mensal</CardTitle>
            <CardDescription>
              Receita total por mês
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[300px] w-full">
                <Skeleton className="h-full w-full" />
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(value) => `R$${value / 1000}k`} />
                    <Tooltip formatter={(value) => [`R$ ${value.toLocaleString('pt-BR')}`, 'Receita']} />
                    <Bar dataKey="revenue" fill="#3b82f6" name="Receita" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Faturas Recentes</CardTitle>
          <CardDescription>
            Últimas faturas emitidas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array(5).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº da Fatura</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Data de Vencimento</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentInvoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-extrabold">{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.customer_name}</TableCell>
                    <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                    <TableCell>{new Date(invoice.due_date).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell><StatusBadge status={invoice.status} context="invoice" /></TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                      >
                        Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => navigate('/invoices')}>
              Ver todas as faturas
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default InvoicesDashboard; 